import { useState, useEffect } from 'react';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  where,
  serverTimestamp,
  orderBy
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Deal, DealData } from '../types/deal';

export const useDeals = (customerId?: string) => {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchDeals = async () => {
      try {
        setLoading(true);
        const dealsRef = collection(db, 'deals');
        let dealsQuery;

        if (customerId) {
          dealsQuery = query(
            dealsRef,
            where('customerId', '==', customerId),
            orderBy('date', 'desc')
          );
        } else {
          dealsQuery = query(
            dealsRef,
            orderBy('date', 'desc')
          );
        }

        const snapshot = await getDocs(dealsQuery);
        const dealsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate(),
          followUpDate: doc.data().followUpDate?.toDate(),
          createdAt: doc.data().createdAt?.toDate(),
          updatedAt: doc.data().updatedAt?.toDate(),
        } as Deal));

        setDeals(dealsList);
      } catch (error) {
        console.error('Error fetching deals:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeals();
  }, [customerId]);

  const addDeal = async (dealData: Omit<DealData, 'createdBy'>) => {
    try {
      const newDeal = {
        ...dealData,
        createdBy: user?.uid || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'deals'), newDeal);

      // Update the local state with the new deal
      const addedDeal: Deal = {
        id: docRef.id,
        ...dealData,
        createdBy: user?.uid || '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setDeals(prev => [addedDeal, ...prev]);
      return addedDeal;
    } catch (error) {
      console.error('Error adding deal:', error);
      throw error;
    }
  };

  const updateDeal = async (id: string, dealData: Partial<DealData>) => {
    try {
      const dealRef = doc(db, 'deals', id);

      await updateDoc(dealRef, {
        ...dealData,
        updatedAt: serverTimestamp()
      });

      // Update the local state
      setDeals(prev =>
        prev.map(deal =>
          deal.id === id
            ? { ...deal, ...dealData, updatedAt: new Date() }
            : deal
        )
      );
    } catch (error) {
      console.error('Error updating deal:', error);
      throw error;
    }
  };

  const deleteDeal = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'deals', id));

      // Update the local state
      setDeals(prev => prev.filter(deal => deal.id !== id));
    } catch (error) {
      console.error('Error deleting deal:', error);
      throw error;
    }
  };

  return {
    deals,
    loading,
    addDeal,
    updateDeal,
    deleteDeal
  };
}; 